/**
 * Цветовые политики секций шаблона карточки (ui_config.color_policy).
 * Классы Tailwind — статические строки (иначе JIT их не подхватит).
 */

export interface SectionColorPolicyOption {
  value: string;
  label: string;
}

/** Порядок — как в селекторе админки. */
export const SECTION_COLOR_POLICY_OPTIONS: SectionColorPolicyOption[] = [
  { value: "neutral", label: "Нейтральная" },
  { value: "slate", label: "Сланец" },
  { value: "stone", label: "Камень" },
  { value: "zinc", label: "Цинк" },
  { value: "blue", label: "Синяя" },
  { value: "sky", label: "Небесная" },
  { value: "cyan", label: "Бирюзовая" },
  { value: "teal", label: "Морская" },
  { value: "green", label: "Зелёная" },
  { value: "emerald", label: "Изумрудная" },
  { value: "lime", label: "Лайм" },
  { value: "yellow", label: "Жёлтая" },
  { value: "amber", label: "Янтарная" },
  { value: "orange", label: "Оранжевая" },
  { value: "red", label: "Красная" },
  { value: "rose", label: "Розовая" },
  { value: "pink", label: "Розовато-малиновая" },
  { value: "fuchsia", label: "Фуксия" },
  { value: "purple", label: "Фиолетовая" },
  { value: "violet", label: "Фиалковая" },
  { value: "indigo", label: "Индиго" },
];

export const DEFAULT_SECTION_COLOR_POLICY = "neutral";

const ACCENT_TRIGGER =
  "flex w-full items-center justify-between px-2 py-1.5 hover:bg-accent text-sm font-semibold";
const ACCENT_HEADER = "text-sm font-semibold text-foreground px-2 py-1.5";

/** Обёртка секции: рамка + лёгкий фон + акцент слева (кроме neutral). */
export const SECTION_COLOR_WRAPPER_CLASSES: Record<string, string> = {
  neutral:
    "rounded-md border border-muted bg-muted/20 overflow-hidden",
  slate:
    "rounded-md border border-slate-500/20 bg-slate-50/40 border-l-4 border-slate-500/70 overflow-hidden dark:bg-slate-950/30 dark:border-slate-400/25",
  stone:
    "rounded-md border border-stone-500/20 bg-stone-50/40 border-l-4 border-stone-500/70 overflow-hidden dark:bg-stone-950/30 dark:border-stone-400/25",
  zinc:
    "rounded-md border border-zinc-500/20 bg-zinc-50/40 border-l-4 border-zinc-500/70 overflow-hidden dark:bg-zinc-950/30 dark:border-zinc-400/25",
  blue:
    "rounded-md border border-blue-500/20 bg-blue-50/30 border-l-4 border-blue-500/70 overflow-hidden dark:bg-blue-950/25 dark:border-blue-400/30",
  sky:
    "rounded-md border border-sky-500/20 bg-sky-50/30 border-l-4 border-sky-500/70 overflow-hidden dark:bg-sky-950/25 dark:border-sky-400/30",
  cyan:
    "rounded-md border border-cyan-500/20 bg-cyan-50/30 border-l-4 border-cyan-500/70 overflow-hidden dark:bg-cyan-950/25 dark:border-cyan-400/30",
  teal:
    "rounded-md border border-teal-500/20 bg-teal-50/30 border-l-4 border-teal-500/70 overflow-hidden dark:bg-teal-950/25 dark:border-teal-400/30",
  green:
    "rounded-md border border-green-500/20 bg-green-50/30 border-l-4 border-green-500/70 overflow-hidden dark:bg-green-950/25 dark:border-green-400/30",
  emerald:
    "rounded-md border border-emerald-500/20 bg-emerald-50/30 border-l-4 border-emerald-500/70 overflow-hidden dark:bg-emerald-950/25 dark:border-emerald-400/30",
  lime:
    "rounded-md border border-lime-500/20 bg-lime-50/35 border-l-4 border-lime-500/70 overflow-hidden dark:bg-lime-950/20 dark:border-lime-400/30",
  yellow:
    "rounded-md border border-yellow-500/25 bg-yellow-50/40 border-l-4 border-yellow-500/70 overflow-hidden dark:bg-yellow-950/20 dark:border-yellow-400/35",
  amber:
    "rounded-md border border-amber-500/20 bg-amber-50/35 border-l-4 border-amber-500/70 overflow-hidden dark:bg-amber-950/25 dark:border-amber-400/30",
  orange:
    "rounded-md border border-orange-500/20 bg-orange-50/30 border-l-4 border-orange-500/70 overflow-hidden dark:bg-orange-950/25 dark:border-orange-400/30",
  red:
    "rounded-md border border-red-500/20 bg-red-50/30 border-l-4 border-red-500/70 overflow-hidden dark:bg-red-950/25 dark:border-red-400/30",
  rose:
    "rounded-md border border-rose-500/20 bg-rose-50/30 border-l-4 border-rose-500/70 overflow-hidden dark:bg-rose-950/25 dark:border-rose-400/30",
  pink:
    "rounded-md border border-pink-500/20 bg-pink-50/30 border-l-4 border-pink-500/70 overflow-hidden dark:bg-pink-950/25 dark:border-pink-400/30",
  fuchsia:
    "rounded-md border border-fuchsia-500/20 bg-fuchsia-50/30 border-l-4 border-fuchsia-500/70 overflow-hidden dark:bg-fuchsia-950/25 dark:border-fuchsia-400/30",
  purple:
    "rounded-md border border-purple-500/20 bg-purple-50/30 border-l-4 border-purple-500/70 overflow-hidden dark:bg-purple-950/25 dark:border-purple-400/30",
  violet:
    "rounded-md border border-violet-500/20 bg-violet-50/30 border-l-4 border-violet-500/70 overflow-hidden dark:bg-violet-950/25 dark:border-violet-400/30",
  indigo:
    "rounded-md border border-indigo-500/20 bg-indigo-50/30 border-l-4 border-indigo-500/70 overflow-hidden dark:bg-indigo-950/25 dark:border-indigo-400/30",
};

export function sectionColorWrapperClass(policy: string | undefined): string {
  const key = policy && SECTION_COLOR_WRAPPER_CLASSES[policy] ? policy : DEFAULT_SECTION_COLOR_POLICY;
  return SECTION_COLOR_WRAPPER_CLASSES[key] ?? SECTION_COLOR_WRAPPER_CLASSES.neutral;
}

export const SECTION_COLOR_TRIGGER_CLASS = ACCENT_TRIGGER;
export const SECTION_COLOR_HEADER_CLASS = ACCENT_HEADER;
